import React from 'react';

// A single improvement-vector row. Used in scan-feedback panels, encouragement
// pickers, and the regimen list. DESIGN.md §6.1 VectorRow (v1.2 premium pass).
//
// Variants:
//   'row'  (default) — flat list row separated by hairlines (Vault feedback panel)
//   'cell' — self-contained rounded cell with border + sheen (Glowups
//            encouragement mockup). Parent supplies vertical gap.
//
// Props:
//   icon: ReactNode             leading 16px outline glyph
//   iconTile: bool              wrap icon in a small rounded bordered tile
//   name: string                small caps tracked label
//   description: string         body Bone Dim subdescription (optional)
//   value: string|number        trailing numeric in Aurum Display Serif (optional)
//   trailing: ReactNode         custom trailing slot (overrides value if provided)
//   onClick: function           makes the whole row a button
//   active: bool                applies the selected style
//   divider: bool               1px Hairline Soft border-bottom (row variant only)
const VectorRow = ({
  icon,
  iconTile = false,
  name,
  description,
  value,
  trailing,
  onClick,
  active = false,
  divider = true,
  variant = 'row',
  className = '',
  'data-testid': testId,
}) => {
  const Wrapper = onClick ? 'button' : 'div';
  const wrapperProps = onClick
    ? { type: 'button', onClick, 'aria-pressed': active }
    : {};

  const isCell = variant === 'cell';

  const shape = isCell
    ? [
        'rounded-field border bg-card-sheen',
        active
          ? 'border-aurum/60 shadow-aurum-glow'
          : 'border-hairline-soft hover:border-hairline',
      ].join(' ')
    : [
        divider ? 'border-b border-hairline-soft' : '',
        onClick ? 'hover:bg-vein/40' : '',
      ].join(' ');

  return (
    <Wrapper
      {...wrapperProps}
      data-testid={testId}
      className={[
        'w-full text-left bg-transparent border-0',
        'flex items-center gap-4',
        'py-3 px-4',
        'transition-all duration-200 ease-mogged',
        onClick ? 'cursor-pointer' : '',
        shape,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {icon && (
        iconTile ? (
          <span
            className={[
              'inline-flex items-center justify-center w-9 h-9 shrink-0',
              'rounded-thumb border border-hairline-soft bg-vein/50',
              active ? 'text-aurum border-aurum/40' : 'text-bone-dim',
            ].join(' ')}
          >
            {icon}
          </span>
        ) : (
          <span className={['inline-flex items-center w-4 h-4 shrink-0', active ? 'text-aurum' : 'text-bone-dim'].join(' ')}>
            {icon}
          </span>
        )
      )}
      <span className="flex-1 min-w-0 flex flex-col">
        <span
          className={[
            'font-display uppercase tracking-[0.10em] text-xs',
            active ? 'text-aurum' : 'text-bone',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {name}
        </span>
        {description && (
          <span
            className="font-display italic text-bone-dim mt-0.5"
            style={{ fontSize: 12, lineHeight: 1.4 }}
          >
            {description}
          </span>
        )}
      </span>
      {trailing != null ? (
        <span className="shrink-0">{trailing}</span>
      ) : value != null ? (
        <span
          className="font-display text-aurum shrink-0 tabular-nums"
          style={{ fontSize: 20, lineHeight: 1 }}
        >
          {value}
        </span>
      ) : null}
    </Wrapper>
  );
};

export default VectorRow;
